import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/DashboardLayout';
import {
  LayoutDashboard, Package, AlertTriangle, Upload, ListTodo,
  FileText, CheckSquare, Settings, Archive, ArrowLeft,
  Shield, Wrench, ClipboardList, GraduationCap, Clock, Users, Menu, X
} from 'lucide-react';

import CloseoutDashboard from '@/components/closeout/CloseoutDashboard';
import CloseoutPackages from '@/components/closeout/CloseoutPackages';
import CloseoutDefects from '@/components/closeout/CloseoutDefects';
import ConsultantUploads from '@/components/closeout/ConsultantUploads';
import ContractRules from '@/components/closeout/ContractRules';
import TaskAllocation from '@/components/closeout/TaskAllocation';
import CloseoutDocuments from '@/components/closeout/CloseoutDocuments';
import CloseoutSignoffs from '@/components/closeout/CloseoutSignoffs';
import CloseoutAssets from '@/components/closeout/CloseoutAssets';
import CloseoutTraining from '@/components/closeout/CloseoutTraining';
import PostHandover from '@/components/closeout/PostHandover';
import CloseoutReports from '@/components/closeout/CloseoutReports';
import SubcontractorPortal from '@/components/closeout/SubcontractorPortal';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'packages', label: 'Packages', icon: Package },
  { id: 'defects', label: 'Live Defects / Snags', icon: AlertTriangle },
  { id: 'uploads', label: 'Consultant Uploads', icon: Upload },
  { id: 'tasks', label: 'Task Allocation', icon: ListTodo },
  { id: 'portal', label: 'Subcontractor Portal', icon: Users },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'signoffs', label: 'Sign-Offs', icon: CheckSquare },
  { id: 'assets', label: 'Assets / Systems', icon: Wrench },
  { id: 'training', label: 'Training', icon: GraduationCap },
  { id: 'post-handover', label: 'Post-Handover', icon: Clock },
  { id: 'reports', label: 'Reports', icon: ClipboardList },
  { id: 'rules', label: 'Templates & Rules', icon: Settings },
  { id: 'archive', label: 'Archive', icon: Archive, coming: true },
];

export default function CloseoutEngine() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const [project, setProject] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projRes, settingsRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.closeout.getProjectSettings(projectId),
      ]);
      setProject(projRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const setTab = (tab) => setSearchParams({ tab });
  const family = settings?.contract_family || project?.contract_family || project?.contract_type || 'JBCC';
  const profile = settings?.profile || {};

  // Dynamic label based on contract
  const defectsLabel = profile.live_defects_enabled ? 'Live Defects / Snags' : profile.snag_label || 'Snags';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <CloseoutDashboard projectId={projectId} settings={settings} onNavigate={setTab} />;
      case 'packages':
        return <CloseoutPackages projectId={projectId} settings={settings} />;
      case 'defects':
        return <CloseoutDefects projectId={projectId} settings={settings} />;
      case 'uploads':
        return <ConsultantUploads projectId={projectId} settings={settings} />;
      case 'tasks':
        return <TaskAllocation projectId={projectId} settings={settings} />;
      case 'portal':
        return <SubcontractorPortal projectId={projectId} settings={settings} />;
      case 'documents':
        return <CloseoutDocuments projectId={projectId} settings={settings} />;
      case 'signoffs':
        return <CloseoutSignoffs projectId={projectId} settings={settings} />;
      case 'assets':
        return <CloseoutAssets projectId={projectId} settings={settings} />;
      case 'training':
        return <CloseoutTraining projectId={projectId} settings={settings} />;
      case 'post-handover':
        return <PostHandover projectId={projectId} settings={settings} />;
      case 'reports':
        return <CloseoutReports projectId={projectId} settings={settings} />;
      case 'rules':
        return <ContractRules projectId={projectId} settings={settings} onSettingsUpdate={loadData} />;
      default:
        return (
          <Card className="mt-8">
            <CardContent className="p-12 text-center">
              <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-lg font-medium text-slate-500">Coming Soon</p>
              <p className="text-sm text-slate-400 mt-1">This section is under development</p>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <DashboardLayout>
    <div className="min-h-screen bg-slate-50" data-testid="closeout-engine">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)} className="gap-1 flex-shrink-0">
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-barlow font-bold uppercase tracking-tight text-slate-900 truncate">
              Close-Out Engine
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 truncate">{project?.name}</p>
          </div>
          <Badge className="bg-slate-900 text-white text-[10px] sm:text-xs uppercase flex-shrink-0">{family}</Badge>
          <Badge variant="outline" className="text-[10px] sm:text-xs hidden sm:inline-flex">
            {profile.completion_term || 'Completion'}
          </Badge>
        </div>
      </div>

      {/* Mobile Nav Tabs (horizontal scroll) */}
      <div className="md:hidden bg-white border-b border-slate-200 overflow-x-auto">
        <div className="flex gap-1 px-2 py-1.5 min-w-max">
          {NAV_ITEMS.filter(i => !i.coming).map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const label = item.id === 'defects' ? defectsLabel : item.label;
            return (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`mobile-nav-${item.id}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex">
        {/* Desktop Left Navigation */}
        <nav className="hidden md:block w-56 bg-white border-r border-slate-200 min-h-[calc(100vh-73px)] flex-shrink-0" data-testid="closeout-nav">
          <div className="py-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              const label = item.id === 'defects' ? defectsLabel : item.label;
              return (
                <button
                  key={item.id}
                  onClick={() => !item.coming && setTab(item.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors ${
                    isActive
                      ? 'bg-slate-900 text-white font-medium'
                      : item.coming
                        ? 'text-slate-300 cursor-default'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={`nav-${item.id}`}
                  disabled={item.coming}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{label}</span>
                  {item.coming && <span className="ml-auto text-[10px] text-slate-300">Soon</span>}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-auto">
          {renderContent()}
        </main>
      </div>
    </div>
    </DashboardLayout>
  );
}
